import React from 'react';
import { Check, Copy } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../supabaseClient';

interface Grant {
  clientId: string;
  name: string;
  grantedAt: string;
}

const formatDate = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('es-AR');
};

// Sección de Ajustes para conectar un asistente por MCP y revocar accesos.
// Sin REACT_APP_MCP_URL solo aparece si hay asistentes conectados, para que
// siempre se pueda quitar un acceso.
export function AssistantConnection() {
  const { theme } = useTheme();
  const mcpUrl = process.env.REACT_APP_MCP_URL;
  const [copied, setCopied] = React.useState(false);
  const [grants, setGrants] = React.useState<Grant[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [revoking, setRevoking] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    supabase.auth.oauth.listGrants().then(({ data, error: listError }) => {
      if (cancelled) return;
      if (listError) {
        setError('No se pudieron cargar los asistentes conectados.');
        setGrants([]);
        return;
      }
      setGrants(
        (data ?? []).map((g) => ({
          clientId: g.client.id,
          name: g.client.name || 'Sin nombre',
          grantedAt: g.granted_at,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!mcpUrl && !grants?.length) return null;

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('No se pudo copiar. Seleccioná la dirección y copiala a mano.');
    }
  };

  const revoke = async (clientId: string) => {
    setRevoking(clientId);
    setError(null);
    const { error: revokeError } = await supabase.auth.oauth.revokeGrant({ clientId });
    setRevoking(null);
    if (revokeError) {
      setError('No se pudo quitar el acceso. Probá de nuevo.');
      return;
    }
    setGrants((current) => (current ?? []).filter((g) => g.clientId !== clientId));
  };

  return (
    <section className={`border-t ${theme.border} pt-3 space-y-2`} aria-labelledby='assistant-heading'>
      <h3 id='assistant-heading' className='font-semibold'>
        {mcpUrl ? 'Conectar asistente (MCP)' : 'Asistentes conectados'}
      </h3>
      {mcpUrl && (
        <>
          <p className={`text-sm ${theme.textSecondary}`}>
            Pegá esta dirección en Claude, ChatGPT u otro cliente MCP e iniciá sesión con tu cuenta.
          </p>
          <div className='flex gap-2'>
            <input
              readOnly
              value={mcpUrl}
              aria-label='Dirección del servidor MCP'
              onFocus={(e) => e.target.select()}
              className={`flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm ${theme.input}`}
            />
            <button
              onClick={() => copy(mcpUrl)}
              className={`px-3 py-2 rounded-lg border ${theme.border} ${theme.hover} flex items-center gap-1 text-sm touch-manipulation`}
            >
              {copied ? <Check className='w-4 h-4' /> : <Copy className='w-4 h-4' />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </>
      )}
      <p className={`text-sm ${theme.textSecondary}`}>
        Un asistente conectado puede ver, modificar y borrar tus rutinas, entrenamientos, planes y
        ejercicios personalizados. Al quitarle el acceso deja de funcionar en menos de una hora.
      </p>

      {grants === null ? (
        <p className={`text-sm ${theme.textSecondary}`}>Cargando asistentes…</p>
      ) : grants.length === 0 ? (
        <p className={`text-sm ${theme.textSecondary}`}>Ningún asistente conectado.</p>
      ) : (
        <ul className='space-y-2'>
          {grants.map((g) => (
            <li key={g.clientId} className='flex items-center justify-between gap-2 text-sm'>
              <span className='min-w-0'>
                <span className='block truncate'>{g.name}</span>
                <span className={`block text-xs ${theme.textSecondary}`}>
                  Desde {formatDate(g.grantedAt)} · {g.clientId.slice(0, 8)}
                </span>
              </span>
              <button
                onClick={() => revoke(g.clientId)}
                disabled={revoking === g.clientId}
                className='px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 touch-manipulation'
              >
                {revoking === g.clientId ? 'Quitando…' : 'Quitar acceso'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p role='alert' className='text-sm text-red-500'>{error}</p>}
    </section>
  );
}
